import classNames from 'classnames';
import {connect} from 'react-redux';
import {compose} from 'redux';
import {defineMessages, FormattedMessage, injectIntl} from 'react-intl';
import intlShape from '../../lib/intlShape.js';
import PropTypes from 'prop-types';
import bindAll from 'lodash.bindall';
import bowser from 'bowser';
import React from 'react';

import VM from '@scratch/scratch-vm';

import Box from '../box/box.jsx';
import Button from '../button/button.jsx';
import {ComingSoonTooltip} from '../coming-soon/coming-soon.jsx';
import Divider from '../divider/divider.jsx';
import LoginDropdown from './login-dropdown.jsx';
import MenuBarHOC from '../../containers/menu-bar-hoc.jsx';
import SettingsMenu from './settings-menu.jsx';
import {PresenceList} from '@juicemind/visual-collab/presence/PresenceList';
import jmHostRelay from '../../playground/jm-host-relay.js';
import {getReadOnly} from '../../playground/jm-read-only.js';
import FileMenu from './file-menu.jsx';
import EditMenu from './edit-menu.jsx';
import ModeMenu from './mode-menu.jsx';
import AboutMenu from './about-menu.jsx';

import {openTipsLibrary, openDebugModal} from '../../reducers/modals';
import {setPlayer} from '../../reducers/mode';
import {
    isTimeTravel220022BC,
    isTimeTravel1920,
    isTimeTravel1990,
    isTimeTravel2020,
    isTimeTravelNow,
    setTimeTravel
} from '../../reducers/time-travel';
import {
    autoUpdateProject,
    getIsUpdating,
    getIsShowingProject,
    requestNewProject,
    manualUpdateProject,
    remixProject
} from '../../reducers/project-state';
import {
    openLoginMenu,
    closeLoginMenu,
    loginMenuOpen
} from '../../reducers/menus';

import collectMetadata from '../../lib/collect-metadata';
import {PLATFORM} from '../../lib/platform';

import styles from './menu-bar.css';

import helpIcon from '../../lib/assets/icon--tutorials.svg';
import mystuffIcon from './icon--mystuff.png';
import remixIcon from './icon--remix.svg';
import debugIcon from '../debug-modal/icons/icon--debug.svg';
import fullscreenIcon from './icon--fullscreen.svg';
import unfullscreenIcon from './icon--unfullscreen.svg';

import scratchLogo from './scratch-logo.svg';
import scratchLogoAndroid from './scratch-logo-android.svg';
import ninetiesLogo from './nineties_logo.svg';
import catLogo from './cat_logo.svg';
import prehistoricLogo from './prehistoric-logo.svg';
import oldtimeyLogo from './oldtimey-logo.svg';

import sharedMessages from '../../lib/shared-messages';

import {AccountMenuOptionsPropTypes} from '../../lib/account-menu-options';
import AccountMenu from './account-menu.jsx';

const ariaMessages = defineMessages({
    tutorials: {
        id: 'gui.menuBar.tutorialsLibrary',
        defaultMessage: 'Tutorials',
        description: 'accessibility text for the tutorials button'
    },
    debug: {
        id: 'gui.menuBar.debug',
        defaultMessage: 'Debug',
        description: 'accessibility text for the debug button'
    },
    fullscreen: {
        id: 'gui.menuBar.fullscreen',
        defaultMessage: 'Fullscreen',
        description: 'accessibility text for the fullscreen button'
    },
    exitFullscreen: {
        id: 'gui.menuBar.exitFullscreen',
        defaultMessage: 'Exit fullscreen',
        description: 'accessibility text for the exit fullscreen button'
    },
    home: {
        id: 'gui.menuBar.home',
        defaultMessage: 'Home',
        description: 'accessibility text for the home button'
    },
    myStuff: {
        id: 'gui.menuBar.myStuff',
        defaultMessage: 'My Stuff',
        description: 'accessibility text for the my stuff button'
    }
});

const getScratchLogo = platform => (platform === PLATFORM.ANDROID ? scratchLogoAndroid : scratchLogo);

const MenuBarItemTooltip = ({
    children,
    className,
    enable,
    id,
    place = 'bottom'
}) => {
    if (enable) {
        return (
            <React.Fragment>
                {children}
            </React.Fragment>
        );
    }
    return (
        <ComingSoonTooltip
            className={classNames(styles.comingSoon, className)}
            place={place}
            tooltipClassName={styles.comingSoonTooltip}
            tooltipId={id}
        >
            {children}
        </ComingSoonTooltip>
    );
};

MenuBarItemTooltip.propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    enable: PropTypes.bool,
    id: PropTypes.string,
    place: PropTypes.oneOf(['top', 'bottom', 'left', 'right'])
};

const MenuItemTooltip = ({id, isRtl, children, className}) => (
    <ComingSoonTooltip
        className={classNames(styles.comingSoon, className)}
        isRtl={isRtl}
        place={isRtl ? 'left' : 'right'}
        tooltipClassName={styles.comingSoonTooltip}
        tooltipId={id}
    >
        {children}
    </ComingSoonTooltip>
);

MenuItemTooltip.propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    id: PropTypes.string,
    isRtl: PropTypes.bool
};

class MenuBar extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'handleClickNew',
            'handleSetMode',
            'handleKeyPress',
            'handleRestoreOption',
            'getSaveToComputerHandler',
            'restoreOptionMessage',
            'handleCollabChange',
            'handleSaveChange',
            'handleHistoryChange',
            'handleFullscreenChange',
            'handleToggleFullscreen'
        ]);
        this.state = {
            collab: (typeof window !== 'undefined' && window.__JM_COLLAB__) || null,
            save: (typeof window !== 'undefined' && window.__JM_SAVE__) || null,
            history: (typeof window !== 'undefined' && window.__JM_HISTORY__) || false,
            isFullscreen: typeof document !== 'undefined' && !!document.fullscreenElement
        };
    }
    handleFullscreenChange () {
        this.setState({isFullscreen: typeof document !== 'undefined' && !!document.fullscreenElement});
    }
    handleToggleFullscreen () {
        if (typeof document === 'undefined') return;
        if (document.fullscreenElement) {
            document.exitFullscreen?.();
        } else {
            document.documentElement.requestFullscreen?.();
        }
    }
    handleCollabChange () {
        this.setState({collab: (typeof window !== 'undefined' && window.__JM_COLLAB__) || null});
    }
    handleSaveChange () {
        this.setState({save: (typeof window !== 'undefined' && window.__JM_SAVE__) || null});
    }
    handleHistoryChange () {
        this.setState({history: (typeof window !== 'undefined' && window.__JM_HISTORY__) || false});
    }
    componentDidMount () {
        document.addEventListener('keydown', this.handleKeyPress);
        document.addEventListener('fullscreenchange', this.handleFullscreenChange);
        window.addEventListener('jm:collab', this.handleCollabChange);
        window.addEventListener('jm:save', this.handleSaveChange);
        window.addEventListener('jm:history', this.handleHistoryChange);
        this.handleCollabChange();
        this.handleSaveChange();
        this.handleHistoryChange();
    }
    componentWillUnmount () {
        document.removeEventListener('keydown', this.handleKeyPress);
        document.removeEventListener('fullscreenchange', this.handleFullscreenChange);
        window.removeEventListener('jm:collab', this.handleCollabChange);
        window.removeEventListener('jm:save', this.handleSaveChange);
        window.removeEventListener('jm:history', this.handleHistoryChange);
    }
    handleClickNew () {
        // if the project is dirty, and user owns the project, we will autosave.
        // but if they are not logged in and can't save, user should consider
        // downloading or logging in first.
        // Note that if user is logged in and editing someone else's project,
        // they'll lose their work.
        const readyToReplaceProject = this.props.confirmReadyToReplaceProject(
            this.props.intl.formatMessage(sharedMessages.replaceProjectWarning)
        );
        if (readyToReplaceProject) {
            this.props.onClickNew(this.props.canSave && this.props.canCreateNew);
        }
    }
    handleSetMode (mode) {
        return () => {
            // Turn on/off filters for modes.
            if (mode === '1920') {
                document.documentElement.style.filter = 'brightness(.9)contrast(.8)sepia(1.0)';
                document.documentElement.style.height = '100%';
            } else if (mode === '1990') {
                document.documentElement.style.filter = 'hue-rotate(40deg)';
                document.documentElement.style.height = '100%';
            } else {
                document.documentElement.style.filter = '';
                document.documentElement.style.height = '';
            }

            // Change logo for modes
            if (mode === '1990') {
                document.getElementById('logo_img').src = ninetiesLogo;
            } else if (mode === '2020') {
                document.getElementById('logo_img').src = catLogo;
            } else if (mode === '1920') {
                document.getElementById('logo_img').src = oldtimeyLogo;
            } else if (mode === '220022BC') {
                document.getElementById('logo_img').src = prehistoricLogo;
            } else {
                document.getElementById('logo_img').src = getScratchLogo(this.props.platform);
            }

            this.props.onSetTimeTravelMode(mode);
        };
    }
    handleRestoreOption (restoreFun) {
        return () => {
            restoreFun();
        };
    }
    handleKeyPress (event) {
        const modifier = bowser.mac ? event.metaKey : event.ctrlKey;
        if (modifier && event.key === 's') {
            this.props.onClickSave();
            event.preventDefault();
        }
    }
    getSaveToComputerHandler (downloadProjectCallback) {
        return () => {
            downloadProjectCallback();
            if (this.props.onProjectTelemetryEvent) {
                const metadata = collectMetadata(this.props.vm, this.props.projectTitle, this.props.locale);
                this.props.onProjectTelemetryEvent('projectDidSave', metadata);
            }
        };
    }
    restoreOptionMessage (deletedItem) {
        switch (deletedItem) {
        case 'Sprite':
            return (<FormattedMessage
                defaultMessage="Restore Sprite"
                description="Menu bar item for restoring the last deleted sprite."
                id="gui.menuBar.restoreSprite"
            />);
        case 'Sound':
            return (<FormattedMessage
                defaultMessage="Restore Sound"
                description="Menu bar item for restoring the last deleted sound."
                id="gui.menuBar.restoreSound"
            />);
        case 'Costume':
            return (<FormattedMessage
                defaultMessage="Restore Costume"
                description="Menu bar item for restoring the last deleted costume."
                id="gui.menuBar.restoreCostume"
            />);
        default: {
            return (<FormattedMessage
                defaultMessage="Restore"
                description="Menu bar item for restoring the last deleted item in its disabled state." /* eslint-disable-line @stylistic/max-len */
                id="gui.menuBar.restore"
            />);
        }
        }
    }
    render () {
        const remixMessage = (
            <FormattedMessage
                defaultMessage="Remix"
                description="Menu bar item for remixing"
                id="gui.menuBar.remix"
            />
        );
        const remixButton = (
            <Button
                className={classNames(
                    styles.menuBarButton,
                    styles.remixButton
                )}
                iconClassName={styles.remixButtonIcon}
                iconSrc={remixIcon}
                onClick={this.props.onClickRemix}
            >
                {remixMessage}
            </Button>
        );

        const menuOpts = this.props.accountMenuOptions;

        return (
            <Box
                className={classNames(
                    this.props.className,
                    styles.menuBar
                )}
                aria-label={this.props.ariaLabel}
                role={this.props.ariaRole}
                element="header"
            >
                <div className={styles.mainMenu}>
                    <div className={styles.fileGroup}>
                        <button
                            aria-label={this.props.intl.formatMessage(ariaMessages.home)}
                            className={classNames(styles.menuBarItem)}
                            onClick={this.props.onClickLogo}
                        >
                            <img
                                id="logo_img"
                                alt="Scratch"
                                className={classNames(styles.scratchLogo, {
                                    [styles.clickable]: typeof this.props.onClickLogo !== 'undefined'
                                })}
                                draggable={false}
                                src={getScratchLogo(this.props.platform)}
                            />
                        </button>
                        {(this.props.canChangeColorMode || this.props.canChangeLanguage || this.props.canChangeTheme) &&
                        (<SettingsMenu
                            canChangeLanguage={this.props.canChangeLanguage}
                            canChangeColorMode={this.props.canChangeColorMode}
                            canChangeTheme={this.props.canChangeTheme}
                            hasActiveMembership={this.props.hasActiveMembership}
                            isRtl={this.props.isRtl}
                            depth={1}
                        />)}
                        {(this.props.canManageFiles && !getReadOnly()) && (<FileMenu
                            onStartSelectingFileUpload={this.props.onStartSelectingFileUpload}
                            onClickNew={this.handleClickNew}
                            onClickRemix={this.props.onClickRemix}
                            onClickSave={this.props.onClickSave}
                            getSaveToComputerHandler={this.getSaveToComputerHandler}
                            canSave={this.props.canSave}
                            canCreateCopy={this.props.canCreateCopy}
                            canRemix={this.props.canRemix}
                            intl={this.props.intl}
                            isRtl={this.props.isRtl}
                            remixMessage={remixMessage}
                            depth={1}
                        />)}
                        {!getReadOnly() && (<EditMenu
                            isRtl={this.props.isRtl}
                            onRestoreOption={this.handleRestoreOption}
                            restoreOptionMessage={this.restoreOptionMessage}
                            depth={1}
                        />)}
                        {this.props.isTotallyNormal && (<ModeMenu
                            onSetMode={this.handleSetMode}
                            modeNow={this.props.modeNow}
                            mode2020={this.props.mode2020}
                            isRtl={this.props.isRtl}
                            depth={1}
                        />)}
                    </div>
                    <div className={classNames(styles.menuBarItem, styles.growable)} />
                    <div className={classNames(styles.menuBarItem)}>
                        {this.props.canRemix ? remixButton : []}
                    </div>
                    {this.state.save ? (
                        <div className={classNames(styles.menuBarItem)}>
                            {this.state.save === 'saving' ?
                                'Saving…' :
                                (this.state.save === 'error' || this.state.save === 'error-dismissed') ?
                                    'Save failed' :
                                    'Saved'}
                        </div>
                    ) : null}
                    <Divider className={classNames(styles.divider)} />
                    <div className={styles.fileGroup}>
                        <button
                            aria-label={this.props.intl.formatMessage(ariaMessages.tutorials)}
                            className={
                                classNames(styles.menuBarItem, styles.noOffset, styles.hoverable, 'tutorials-button')
                            }
                            onClick={this.props.onOpenTipLibrary}
                        >
                            <img
                                className={styles.helpIcon}
                                src={helpIcon}
                            />
                            <span className={styles.tutorialsLabel}>
                                <FormattedMessage {...ariaMessages.tutorials} />
                            </span>
                        </button>
                        <button
                            aria-label={this.props.intl.formatMessage(ariaMessages.debug)}
                            className={classNames(styles.menuBarItem, styles.noOffset, styles.hoverable)}
                            onClick={this.props.onOpenDebugModal}
                        >
                            <img
                                className={styles.helpIcon}
                                src={debugIcon}
                            />
                            <span className={styles.debugLabel}>
                                <FormattedMessage {...ariaMessages.debug} />
                            </span>
                        </button>
                        <button
                            aria-label={this.props.intl.formatMessage(
                                this.state.isFullscreen ? ariaMessages.exitFullscreen : ariaMessages.fullscreen
                            )}
                            className={classNames(styles.menuBarItem, styles.noOffset, styles.hoverable)}
                            onClick={this.handleToggleFullscreen}
                        >
                            <img
                                className={styles.helpIcon}
                                src={this.state.isFullscreen ? unfullscreenIcon : fullscreenIcon}
                            />
                            <span className={styles.debugLabel}>
                                <FormattedMessage
                                    {...(this.state.isFullscreen ? ariaMessages.exitFullscreen : ariaMessages.fullscreen)}
                                />
                            </span>
                        </button>
                    </div>
                </div>

                {/* show the proper UI in the account menu, given whether the user is
                logged in, and whether a session is available to log in with */}
                <div className={styles.accountInfoGroup}>
                    {(this.state.history && !getReadOnly()) || this.state.collab ? (
                        <div
                            className={classNames(styles.menuBarItem)}
                            style={{display: 'flex', alignItems: 'center', gap: '8px'}}
                        >
                            {this.state.history && !getReadOnly() ? (
                                <button
                                    className={classNames(styles.menuBarItem, styles.noOffset, styles.hoverable)}
                                    title="Version history"
                                    aria-label="Version history"
                                    onClick={() => jmHostRelay.post({type: 'SCRATCH_OPEN_HISTORY'})}
                                >
                                    <svg
                                        width="20"
                                        height="20"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        style={{display: 'block'}}
                                    >
                                        <path d="M3 3v5h5" />
                                        <path d="M3.05 13A9 9 0 1 0 6 5.3L3 8" />
                                        <path d="M12 7v5l3 2" />
                                    </svg>
                                </button>
                            ) : null}
                            {this.state.collab ? (
                                <PresenceList
                                    awareness={this.state.collab.awareness}
                                    currentUserId={this.state.collab.currentUserId}
                                />
                            ) : null}
                        </div>
                    ) : null}
                    {menuOpts.canHaveSession ? (
                        this.props.username ? (
                            // ************ user is logged in ************
                            <React.Fragment>
                                {menuOpts.myStuffUrl ? (
                                    <a
                                        href={menuOpts.myStuffUrl}
                                        aria-label={this.props.intl.formatMessage(ariaMessages.myStuff)}
                                    >
                                        <div
                                            className={classNames(
                                                styles.menuBarItem,
                                                styles.hoverable,
                                                styles.mystuffButton
                                            )}
                                        >
                                            <img
                                                className={styles.mystuffIcon}
                                                src={mystuffIcon}
                                            />
                                        </div>
                                    </a>
                                ) : null}

                                <AccountMenu
                                    menuOpts={menuOpts}
                                    username={this.props.username}
                                    isRtl={this.props.isRtl}
                                    onLogOut={this.props.onLogOut}
                                    avatarBadge={this.props.avatarBadge}
                                    depth={1}
                                />
                            </React.Fragment>
                        ) : (
                            // ********* user not logged in, but a session exists
                            // ********* so they can choose to log in
                            <React.Fragment>
                                {menuOpts.canRegister ? (
                                    <button
                                        className={classNames(
                                            styles.menuBarItem,
                                            styles.hoverable
                                        )}
                                        key="join"
                                        onClick={this.props.onOpenRegistration}
                                    >
                                        <FormattedMessage
                                            defaultMessage="Join Scratch"
                                            description="Link for creating a Scratch account"
                                            id="gui.menuBar.joinScratch"
                                        />
                                    </button>
                                ) : null}

                                {menuOpts.canLogin ? (
                                    <button
                                        className={classNames(
                                            styles.menuBarItem,
                                            styles.hoverable
                                        )}
                                        key="login"
                                        onMouseUp={this.props.onClickLogin}
                                        onClick={this.props.onClickLogin}
                                    >
                                        <FormattedMessage
                                            defaultMessage="Sign in"
                                            description="Link for signing in to your Scratch account"
                                            id="gui.menuBar.signIn"
                                        />
                                        <LoginDropdown
                                            className={classNames(styles.menuBarMenu)}
                                            isOpen={this.props.loginMenuOpen}
                                            isRtl={this.props.isRtl}
                                            renderLogin={this.props.renderLogin}
                                            onClose={this.props.onRequestCloseLogin}
                                        />
                                    </button>
                                ) : null}
                            </React.Fragment>
                        )
                    ) : null}
                </div>

                {this.props.onClickAbout && (
                    <AboutMenu
                        onClick={this.props.onClickAbout}
                        isRtl={this.props.isRtl}
                        depth={1}
                    />
                )}
            </Box>
        );
    }
}

MenuBar.propTypes = {
    accountMenuOpen: PropTypes.bool,
    ariaLabel: PropTypes.string,
    ariaRole: PropTypes.string,
    authorId: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    authorThumbnailUrl: PropTypes.string,
    authorUsername: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    authorAvatarBadge: PropTypes.number,
    autoUpdateProject: PropTypes.func,
    canChangeLanguage: PropTypes.bool,
    canChangeColorMode: PropTypes.bool,
    canChangeTheme: PropTypes.bool,
    canCreateCopy: PropTypes.bool,
    canCreateNew: PropTypes.bool,
    canEditTitle: PropTypes.bool,
    canManageFiles: PropTypes.bool,
    canRemix: PropTypes.bool,
    canSave: PropTypes.bool,
    canShare: PropTypes.bool,
    className: PropTypes.string,
    confirmReadyToReplaceProject: PropTypes.func,
    currentLocale: PropTypes.string.isRequired,
    enableCommunity: PropTypes.bool,
    hasActiveMembership: PropTypes.bool,
    intl: intlShape,
    isRtl: PropTypes.bool,
    isShared: PropTypes.bool,
    isShowingProject: PropTypes.bool,
    isTotallyNormal: PropTypes.bool,
    isUpdating: PropTypes.bool,
    locale: PropTypes.string.isRequired,
    loginMenuOpen: PropTypes.bool,
    logo: PropTypes.string,
    mode1920: PropTypes.bool,
    mode1990: PropTypes.bool,
    mode2020: PropTypes.bool,
    mode220022BC: PropTypes.bool,
    modeNow: PropTypes.bool,
    onClickAbout: PropTypes.oneOfType([
        PropTypes.func, // button mode: call this callback when the About button is clicked
        PropTypes.arrayOf( // menu mode: list of items in the About menu
            PropTypes.shape({
                title: PropTypes.string, // text for the menu item
                onClick: PropTypes.func // call this callback when the menu item is clicked
            })
        )
    ]),
    onClickLogin: PropTypes.func,
    onClickLogo: PropTypes.func,
    onClickMode: PropTypes.func,
    onClickNew: PropTypes.func,
    onClickRemix: PropTypes.func,
    onClickSave: PropTypes.func,
    onClickSaveAsCopy: PropTypes.func,
    onLogOut: PropTypes.func,
    onOpenRegistration: PropTypes.func,
    onOpenTipLibrary: PropTypes.func,
    onOpenDebugModal: PropTypes.func,
    onProjectTelemetryEvent: PropTypes.func,
    onRequestCloseLogin: PropTypes.func,
    onSeeCommunity: PropTypes.func,
    onSetTimeTravelMode: PropTypes.func,
    onShare: PropTypes.func,
    onStartSelectingFileUpload: PropTypes.func,
    onToggleLoginOpen: PropTypes.func,
    platform: PropTypes.oneOf(Object.keys(PLATFORM)),
    projectTitle: PropTypes.string,
    renderLogin: PropTypes.func,
    shouldSaveBeforeTransition: PropTypes.func,
    showComingSoon: PropTypes.bool,
    username: PropTypes.string,
    avatarBadge: PropTypes.number,
    userOwnsProject: PropTypes.bool,

    accountMenuOptions: AccountMenuOptionsPropTypes,

    vm: PropTypes.instanceOf(VM).isRequired
};

MenuBar.defaultProps = {
    logo: scratchLogo,
    onShare: () => {}
};

const mapStateToProps = (state, ownProps) => {
    const loadingState = state.scratchGui.projectState.loadingState;
    const user = state.session && state.session.session && state.session.session.user;
    const permissions = state.session && state.session.permissions;
    const sessionExists = state.session && typeof state.session.session !== 'undefined';

    return {
        currentLocale: state.locales.locale,
        isRtl: state.locales.isRtl,
        isUpdating: getIsUpdating(loadingState),
        isShowingProject: getIsShowingProject(loadingState),
        locale: state.locales.locale,
        loginMenuOpen: loginMenuOpen(state),
        projectTitle: state.scratchGui.projectTitle,
        username: ownProps.username ?? (user ? user.username : null),
        avatarBadge: ownProps.avatarBadge ?? (user ? user.membership_avatar_badge : null),
        userIsEducator: permissions && permissions.educator,
        vm: state.scratchGui.vm,
        mode220022BC: isTimeTravel220022BC(state),
        mode1920: isTimeTravel1920(state),
        mode1990: isTimeTravel1990(state),
        mode2020: isTimeTravel2020(state),
        modeNow: isTimeTravelNow(state),

        platform: state.scratchGui.platform.platform,

        userOwnsProject: ownProps.userOwnsProject ?? (
            ownProps.authorUsername && user && (ownProps.authorUsername === user.username)
        ),

        accountMenuOptions: ownProps.accountMenuOptions ?? {
            canHaveSession: sessionExists ?? false,

            canRegister: true,
            canLogin: true,
            canLogout: true,

            avatarUrl: user?.thumbnailUrl,
            myStuffUrl: '/mystuff/',
            profileUrl: user && `/users/${user.username}`,
            myClassesUrl: permissions?.educator ? '/educators/classes/' : null,
            myClassUrl: user && permissions?.student ? `/classes/${user.classroomId}/` : null,
            accountSettingsUrl: '/accounts/settings/'
        }
    };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
    autoUpdateProject: () => dispatch(autoUpdateProject()),
    onOpenTipLibrary: () => dispatch(openTipsLibrary()),
    onOpenDebugModal: () => dispatch(openDebugModal()),
    onClickNew: needSave => dispatch(requestNewProject(needSave)),
    onClickLogin: ownProps.onClickLogin ?? (() => dispatch(openLoginMenu())),
    onClickSave: () => dispatch(manualUpdateProject()),
    onClickRemix: () => dispatch(remixProject()),
    onRequestCloseLogin: () => dispatch(closeLoginMenu()),
    onSeeCommunity: ownProps.onSeeCommunity ?? (() => dispatch(setPlayer(true))),
    onSetTimeTravelMode: mode => dispatch(setTimeTravel(mode))
});

export default compose(
    injectIntl,
    MenuBarHOC,
    connect(
        mapStateToProps,
        mapDispatchToProps
    )
)(MenuBar);
